import { ImageToImageConfigType } from "../models/image-to-image";
import { IMAGE_EDITING_FEATURES, type ImageEditingFeature } from "./routesMapper";

type FeatureDetector = (config: ImageToImageConfigType) => boolean;

const FEATURE_DETECTORS: Record<ImageEditingFeature, FeatureDetector> = {
	[IMAGE_EDITING_FEATURES.REMOVE_BACKGROUND]: (config) => !!config.background?.remove,
	[IMAGE_EDITING_FEATURES.BLUR_BACKGROUND]: (config) => !!config.background?.blur,
	[IMAGE_EDITING_FEATURES.EXPAND_BACKGROUND]: (config) => !!config.background?.expand,
	[IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_TEXT]: (config) => !!config.background?.generateByText?.selected,
	[IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_IMAGE]: (config) => !!config.background?.generateByImage?.selected,
	[IMAGE_EDITING_FEATURES.SOLID_BACKGROUND]: (config) => !!config.background?.solid?.selected,
	[IMAGE_EDITING_FEATURES.FOREGROUND_PLACEMENT]: (config) => !!config.foreground_placement?.placements?.selected,
	[IMAGE_EDITING_FEATURES.ERASE_OBJECT]: (config) => !!config.object_manipulation?.erase,
	[IMAGE_EDITING_FEATURES.GENERATIVE_FILL]: (config) => !!config.object_manipulation?.gen_fill,
	[IMAGE_EDITING_FEATURES.CHANGE_ASPECT_RATIO]: (config) => !!config.size?.aspect_ratio?.selected,
	[IMAGE_EDITING_FEATURES.MEDIUM_DESTINATION]: (config) => !!config.size?.medium_destination?.selected,
	[IMAGE_EDITING_FEATURES.INCREASE_RESOLUTION]: (config) => !!config.size?.increase_resolution?.selected,
	[IMAGE_EDITING_FEATURES.CUSTOM_SIZE]: (config) => !!config.size?.custom_size?.selected,
	[IMAGE_EDITING_FEATURES.IMAGE_EDITING]: () => false,
	[IMAGE_EDITING_FEATURES.BULK_IMAGE_EDITING]: () => false,
	[IMAGE_EDITING_FEATURES.CROP_IMAGE]: (config) => !!config.size?.crop || !!config.size?.custom_size?.selected,
};

const SINGLE_MODE_FEATURES: ImageEditingFeature[] = [
	IMAGE_EDITING_FEATURES.REMOVE_BACKGROUND,
	IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_TEXT,
	IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_IMAGE,
	IMAGE_EDITING_FEATURES.EXPAND_BACKGROUND,
	IMAGE_EDITING_FEATURES.INCREASE_RESOLUTION,
	IMAGE_EDITING_FEATURES.CHANGE_ASPECT_RATIO,
	IMAGE_EDITING_FEATURES.CUSTOM_SIZE,
	IMAGE_EDITING_FEATURES.CROP_IMAGE,
	IMAGE_EDITING_FEATURES.SOLID_BACKGROUND,
	IMAGE_EDITING_FEATURES.BLUR_BACKGROUND,
	IMAGE_EDITING_FEATURES.ERASE_OBJECT,
	IMAGE_EDITING_FEATURES.GENERATIVE_FILL,
];

const BULK_MODE_FEATURES: ImageEditingFeature[] = [
	IMAGE_EDITING_FEATURES.REMOVE_BACKGROUND,
	IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_TEXT,
	IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_IMAGE,
	IMAGE_EDITING_FEATURES.EXPAND_BACKGROUND,
	IMAGE_EDITING_FEATURES.FOREGROUND_PLACEMENT,
	IMAGE_EDITING_FEATURES.INCREASE_RESOLUTION,
	IMAGE_EDITING_FEATURES.CHANGE_ASPECT_RATIO,
	IMAGE_EDITING_FEATURES.MEDIUM_DESTINATION,
	IMAGE_EDITING_FEATURES.CUSTOM_SIZE,
	IMAGE_EDITING_FEATURES.SOLID_BACKGROUND,
	IMAGE_EDITING_FEATURES.BLUR_BACKGROUND,
];

export class ImageEditingFeatureDetector {
	static detectSingleModeFeature(config: ImageToImageConfigType): string {
		for (const feature of SINGLE_MODE_FEATURES) {
			if (FEATURE_DETECTORS[feature](config)) {
				const displayNames = this.getFeatureDisplayNames();
				return displayNames[feature];
			}
		}
		return this.getFeatureDisplayNames()[IMAGE_EDITING_FEATURES.IMAGE_EDITING];
	}

	static detectBulkModeFeatures(config: ImageToImageConfigType): string {
		const activeFeatures = BULK_MODE_FEATURES.filter((feature) => {
			const isActive = FEATURE_DETECTORS[feature](config);
			return isActive;
		});

		if (activeFeatures.length === 0) {
			return this.getFeatureDisplayNames()[IMAGE_EDITING_FEATURES.BULK_IMAGE_EDITING];
		}

		const displayNames = this.getFeatureDisplayNames();
		return activeFeatures.map((feature) => displayNames[feature]).join(", ");
	}

	static detectBulkModeFeaturesArray(config: ImageToImageConfigType): string[] {
		const activeFeatures = BULK_MODE_FEATURES.filter((feature) => FEATURE_DETECTORS[feature](config));

		if (activeFeatures.length === 0) {
			return [this.getFeatureDisplayNames()[IMAGE_EDITING_FEATURES.BULK_IMAGE_EDITING]];
		}

		const displayNames = this.getFeatureDisplayNames();
		return activeFeatures.map((feature) => displayNames[feature]);
	}

	static getFeatureString(config: ImageToImageConfigType, isBulkMode: boolean): string {
		return isBulkMode ? this.detectBulkModeFeatures(config) : this.detectSingleModeFeature(config);
	}

	static getFeatureDisplayNames(): Record<ImageEditingFeature, string> {
		return {
			[IMAGE_EDITING_FEATURES.REMOVE_BACKGROUND]: "Remove Background",
			[IMAGE_EDITING_FEATURES.BLUR_BACKGROUND]: "Blur Background",
			[IMAGE_EDITING_FEATURES.EXPAND_BACKGROUND]: "Expand Background",
			[IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_TEXT]: "Generate Background by Text",
			[IMAGE_EDITING_FEATURES.GENERATE_BACKGROUND_BY_IMAGE]: "Generate Background by Image",
			[IMAGE_EDITING_FEATURES.SOLID_BACKGROUND]: "Solid Background",
			[IMAGE_EDITING_FEATURES.FOREGROUND_PLACEMENT]: "Foreground Placement",
			[IMAGE_EDITING_FEATURES.ERASE_OBJECT]: "Erase Object",
			[IMAGE_EDITING_FEATURES.GENERATIVE_FILL]: "Generative Fill",
			[IMAGE_EDITING_FEATURES.CHANGE_ASPECT_RATIO]: "Change Aspect Ratio",
			[IMAGE_EDITING_FEATURES.MEDIUM_DESTINATION]: "Medium Destination",
			[IMAGE_EDITING_FEATURES.INCREASE_RESOLUTION]: "Increase Resolution",
			[IMAGE_EDITING_FEATURES.CUSTOM_SIZE]: "Custom Size",
			[IMAGE_EDITING_FEATURES.IMAGE_EDITING]: "Image Editing",
			[IMAGE_EDITING_FEATURES.BULK_IMAGE_EDITING]: "Bulk Image Editing",
			[IMAGE_EDITING_FEATURES.CROP_IMAGE]: "Crop Image",
		};
	}
}
